import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';

/**
 * 🛡️ Guardian Status Indicator (Feb 13, 2026)
 * 
 * Displays a pulsating indicator showing protocol health status:
 * - ✅ Green pulsating = All systems healthy
 * - 🛑 Red floating = Something is broken or misconfigured
 * 
 * Polls the /api/guardian/status endpoint every 60 seconds
 */
const GuardianStatus = () => {
  const [status, setStatus] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/guardian/status`);
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
        setError(null);
      } else {
        setError('Failed to fetch status');
        setStatus({
          healthy: false,
          emoji: '🛑',
          status: 'ERROR',
          summary: 'Unable to reach server'
        });
      }
    } catch (err) {
      setError(err.message);
      setStatus({
        healthy: false,
        emoji: '🛑',
        status: 'ERROR',
        summary: 'Connection error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initial fetch
    fetchStatus();
    
    // Poll every 60 seconds
    const interval = setInterval(fetchStatus, 60000);
    
    return () => clearInterval(interval);
  }, [fetchStatus]);

  if (loading) {
    return (
      <div style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'pointer'
      }}>
        <div style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          background: '#64748b',
          animation: 'pulse 1.5s ease-in-out infinite'
        }} />
      </div>
    );
  }

  const isHealthy = status?.healthy;
  
  // Get failed checks for display when unhealthy
  const failedChecks = status?.checks?.filter(c => c.status === 'FAIL') || [];

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Main Indicator */}
      <div
        onClick={() => setShowDetails(!showDetails)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          cursor: 'pointer',
          padding: '0.25rem 0.5rem',
          borderRadius: '8px',
          background: isHealthy ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          border: `1px solid ${isHealthy ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
          transition: 'all 0.3s ease'
        }}
        title={status?.summary || 'Click to view details'}
      >
        {/* Pulsating/Floating Icon */}
        <div style={{
          position: 'relative',
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Pulse ring for healthy */}
          {isHealthy && (
            <div style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: 'rgba(34, 197, 94, 0.3)',
              animation: 'guardianPulse 2s ease-in-out infinite'
            }} />
          )}
          
          {/* Float animation for error */}
          <span style={{
            fontSize: '1.25rem',
            animation: isHealthy ? 'none' : 'guardianFloat 1s ease-in-out infinite',
            zIndex: 1
          }}>
            {status?.emoji || (isHealthy ? '✅' : '🛑')}
          </span>
        </div>
        
        {/* Status Text */}
        <span style={{
          fontSize: '0.75rem',
          fontWeight: '600',
          color: isHealthy ? '#22c55e' : '#ef4444',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {isHealthy ? 'Protocol OK' : 'Check Required'}
        </span>
      </div>
      
      {/* Failed Checks Summary - Always visible when unhealthy */}
      {!isHealthy && failedChecks.length > 0 && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.5rem 0.75rem',
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          maxWidth: '300px'
        }}>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: '700',
            color: '#ef4444',
            marginBottom: '0.35rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            🚨 {failedChecks.length} Failed Check{failedChecks.length > 1 ? 's' : ''}:
          </div>
          {failedChecks.slice(0, 5).map((check, idx) => (
            <div 
              key={idx}
              style={{
                fontSize: '0.7rem',
                color: '#fca5a5',
                padding: '0.2rem 0',
                borderTop: idx > 0 ? '1px solid rgba(239, 68, 68, 0.2)' : 'none',
                lineHeight: '1.3'
              }}
            >
              <span style={{ fontWeight: '600', color: '#f87171' }}>• {check.name}</span>
              <div style={{ 
                fontSize: '0.65rem', 
                color: '#fca5a5',
                marginLeft: '0.5rem',
                opacity: 0.9
              }}>
                {check.actual}
              </div>
            </div>
          ))}
          {failedChecks.length > 5 && (
            <div style={{
              fontSize: '0.65rem',
              color: '#f87171',
              marginTop: '0.25rem',
              fontStyle: 'italic'
            }}>
              +{failedChecks.length - 5} more... (click to see all)
            </div>
          )}
        </div>
      )}

      {/* Details Popup */}
      {showDetails && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '0.5rem',
          width: '320px',
          background: '#1a1a2e',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '1rem',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
          zIndex: 1000
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '0.75rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <span style={{ fontWeight: '700', color: '#fff' }}>
              🛡️ Guardian Status
            </span>
            <span style={{
              fontSize: '0.7rem',
              color: isHealthy ? '#22c55e' : '#ef4444',
              fontWeight: '600'
            }}>
              {status?.summary}
            </span>
          </div>

          {/* Checks List */}
          <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {status?.checks?.map((check, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                  padding: '0.5rem 0',
                  borderBottom: idx < status.checks.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none'
                }}
              >
                <span style={{ fontSize: '0.9rem' }}>
                  {check.status === 'PASS' ? '✅' : '🛑'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    color: check.status === 'PASS' ? '#22c55e' : '#ef4444'
                  }}>
                    {check.name}
                  </div>
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#94a3b8',
                    marginTop: '2px'
                  }}>
                    {check.description}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{
            marginTop: '0.75rem',
            paddingTop: '0.5rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            fontSize: '0.65rem',
            color: '#64748b',
            textAlign: 'center'
          }}>
            Last checked: {status?.timestamp ? new Date(status.timestamp).toLocaleTimeString() : 'N/A'}
            <br />
            <span style={{ color: '#94a3b8' }}>{status?.version}</span>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes guardianPulse {
          0% {
            transform: scale(1);
            opacity: 0.7;
          }
          50% {
            transform: scale(1.5);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 0.7;
          }
        }
        
        @keyframes guardianFloat {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }
      `}</style>
    </div>
  );
};

export default GuardianStatus;
